/**
 * Status Mappings and Constants
 * Centralized status definitions and utilities
 */

/**
 * Status types and their display configurations
 */
export const STATUS_CONFIG = {
  'Not Started': {
    label: 'Not Started',
    variant: 'default',
    color: 'gray',
    bgClass: 'bg-gray-200 dark:bg-gray-700',
    textClass: 'text-gray-800 dark:text-gray-200',
    percent: 0,
  },
  'In Progress': {
    label: 'In Progress',
    variant: 'warning',
    color: 'yellow',
    bgClass: 'bg-yellow-200 dark:bg-yellow-900',
    textClass: 'text-yellow-800 dark:text-yellow-200',
    percent: 50,
  },
  'On Hold': {
    label: 'On Hold',
    variant: 'orange',
    color: 'orange',
    bgClass: 'bg-orange-200 dark:bg-orange-900',
    textClass: 'text-orange-800 dark:text-orange-200',
    percent: 25,
  },
  'Completed': {
    label: 'Completed',
    variant: 'success',
    color: 'green',
    bgClass: 'bg-green-200 dark:bg-green-900',
    textClass: 'text-green-800 dark:text-green-200',
    percent: 100,
  },
  'Complete': {
    label: 'Complete',
    variant: 'success',
    color: 'green',
    bgClass: 'bg-green-200 dark:bg-green-900',
    textClass: 'text-green-800 dark:text-green-200',
    percent: 100,
  },
};

/**
 * Status options for select dropdowns
 */
export const STATUS_OPTIONS = [
  'Not Started',
  'In Progress',
  'On Hold',
  'Completed',
];

/**
 * Get status configuration
 * @param {string} status - Status string
 * @returns {Object} - Status configuration
 */
export const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || STATUS_CONFIG['Not Started'];
};

/**
 * Get percent value from status
 * @param {string} status - Status string
 * @returns {number} - Percentage value
 */
export const statusToPercent = (status) => {
  return getStatusConfig(status).percent;
};

/**
 * Get badge classes for status
 * @param {string} status - Status string
 * @returns {string} - Tailwind classes for badge
 */
export const getStatusBadgeClass = (status) => {
  const config = getStatusConfig(status);
  return `${config.bgClass} ${config.textClass}`;
};

/**
 * Task categories used in onboarding
 */
export const TASK_CATEGORIES = {
  INTERNAL_SYSTEMS: ['PSA', 'AEM', 'Fincon', 'IT Glue'],
  COMPANY_WIDE: [
    'Include Backups',
    'Website Migration',
    'Domain DNS',
    'Email Migration',
    'Acronis Advanced Email Security',
  ],
  BRANCH_SERVICES: [
    'Install MiBOT Agent',
    'Install Managed AV',
    'Setup Office 365 / Mail',
    'Setup OneDrive',
    'Install CyberFox',
    'Install Acronis Backup Agent',
  ],
};

/**
 * Field mapping for branch services
 */
export const SERVICE_FIELD_MAP = {
  'Install MiBOT Agent': 'agentInstalled',
  'Install Managed AV': 'managedAVInstalled',
  'Setup Office 365 / Mail': 'mailSetup',
  'Setup OneDrive': 'oneDriveSetup',
  'Install CyberFox': 'cyberFoxInstalled',
  'Install Acronis Backup Agent': 'acronisSetup',
};

/**
 * Normalize service titles so variants map consistently.
 * Example: "CyberFox Security" and "Install CyberFox" -> "cyberfox"
 */
export function normalizeServiceTitle(serviceTitle = '') {
  const raw = String(serviceTitle || '').trim();
  if (!raw) return '';

  return raw
    .replace(/^(install|setup)\s+/i, '')
    .replace(/\bsecurity\b/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Map a branch service title to the list of user task fieldKeys that must be completed
 * when that service is enabled for the branch.
 *
 * Completion rule (used by progress): tri-state "Yes" or "N/A" counts as done.
 */
export function serviceTitleToUserFieldKeys(serviceTitle) {
  const key = normalizeServiceTitle(serviceTitle);
  if (!key) return [];

  // NOTE: These keys correspond to the branch_user dynamic entity fields / legacy BranchUser columns.
  // We keep this mapping tolerant of different service titles as templates evolve.
  const mapping = {
    'mibot agent': ['agentInstalled'],
    'managed av': ['managedAVInstalled', 'oldAVRemoved'],
    'office 365 / mail': ['mailSetup'],
    'onedrive': ['oneDriveSetup'],
    'cyberfox': ['cyberFoxInstalled'],
    'acronis backup agent': ['acronisSetup'],

    // Optional service: only counts if a matching field exists in the entity definition.
    // We include a couple of common candidate keys and later intersect with actual field definitions.
    'itsmine': ['itsMineInstalled', 'itsMineSetup'],
  };

  return mapping[key] || [];
}

export function buildAllowedUserFieldKeysFromServices(serviceTitles = []) {
  const set = new Set();
  (serviceTitles || []).forEach((svc) => {
    serviceTitleToUserFieldKeys(svc).forEach((fieldKey) => set.add(fieldKey));
  });
  return Array.from(set);
}

const statusMappings = {
  STATUS_CONFIG,
  STATUS_OPTIONS,
  getStatusConfig,
  statusToPercent,
  getStatusBadgeClass,
  TASK_CATEGORIES,
  SERVICE_FIELD_MAP,
  normalizeServiceTitle,
  serviceTitleToUserFieldKeys,
  buildAllowedUserFieldKeysFromServices,
};

export default statusMappings;

